import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode } from "vue"

import { ref, reactive, watch } from 'vue';
import type { FormInstance, FormRules } from 'element-plus';
import { ElMessage } from 'element-plus';
import type { Interview } from '@/types/interview';
import { INTERVIEW_STAGES, INTERVIEW_STATUS } from '@/types/interview';
import interviewService from '@/services/interviewService';


export default /*@__PURE__*/_defineComponent({
  __name: 'InterviewForm',
  props: {
    positionId: {},
    interview: {},
    editing: { type: Boolean }
  },
  emits: ["created", "updated", "cancel"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const formRef = ref<FormInstance>();
const loading = ref(false);

// 初始化表单数据
const initialFormData = () => {
  const baseData = {
    stage: 'not_started',
    dateTime: '',
    duration: 60,
    location: '',
    interviewer: '',
    notes: '',
    status: 'pending',
    preparationMaterials: '',
    feedback: ''
  };

  // 只在创建时添加positionId
  return props.editing ? baseData : { ...baseData, positionId: props.positionId };
};

const formData = reactive<Interview>(initialFormData());

// 禁用过去的日期
const disabledDate = (time: Date) => {
  return time.getTime() < Date.now() - 8.64e7; // 禁用今天之前的日期
};

// 当编辑模式且提供了面试数据时，填充表单
watch(
  () => props.interview,
  (newVal) => {
    if (newVal && props.editing) {
      Object.assign(formData, { ...initialFormData(), ...newVal });
    }
  },
  { immediate: true }
);

// 表单验证规则
const rules = reactive<FormRules>({
  stage: [{ required: true, message: '请选择面试阶段', trigger: 'change' }],
  dateTime: [{ required: true, message: '请选择面试时间', trigger: 'change' }],
  duration: [{ type: 'number', message: '面试时长必须为数字', trigger: 'blur' }],
  location: [{ max: 100, message: '面试地点不能超过100个字符', trigger: 'blur' }],
  interviewer: [{ max: 50, message: '面试官姓名不能超过50个字符', trigger: 'blur' }],
  preparationMaterials: [{ max: 500, message: '准备材料不能超过500个字符', trigger: 'blur' }],
  notes: [{ max: 500, message: '备注不能超过500个字符', trigger: 'blur' }],
  feedback: [{ max: 1000, message: '面试反馈不能超过1000个字符', trigger: 'blur' }]
});

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return;
  
  try {
    const valid = await formRef.value.validate();
    if (!valid) {
      console.warn('表单验证未通过:', formRef.value.getValidateFields());
      ElMessage.warning('请完善表单信息');
      return;
    }

    loading.value = true;
    console.log('提交面试表单数据:', JSON.stringify(formData));
    let response;
    
    if (props.editing && props.interview?.id) {
      // 更新面试时，从formData中提取需要的字段
      const { positionId, createTime, updateTime, jobPosition, ...updateData } = formData;
      response = await interviewService.updateInterview({
        id: props.interview.id,
        ...updateData
      });
    } else {
      // 创建面试时使用完整的formData
      response = await interviewService.createInterview(formData);
    }

    ElMessage.success(props.editing ? '面试更新成功' : '面试创建成功');
    // 重置表单
    resetForm();
    // 发送更新事件并传递响应数据
    if (props.editing) {
      emit('updated', response);
    } else {
      emit('created', response);
    }
  } catch (error) {
    console.error('面试操作失败:', error);
    ElMessage.error('操作失败，请重试');
  } finally {
    loading.value = false;
  }
};

// 重置表单
const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields();
  }
};

return (_ctx: any,_cache: any) => {
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_input_number = _resolveComponent("el-input-number")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_form = _resolveComponent("el-form")!

  return (_openBlock(), _createBlock(_component_el_form, {
    ref_key: "formRef",
    ref: formRef,
    model: formData,
    rules: rules,
    "label-width": "120px",
    class: "interview-form"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_el_form_item, {
        label: "面试阶段",
        prop: "stage"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_el_select, {
            modelValue: formData.stage,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.stage) = $event)),
            placeholder: "请选择面试阶段",
            clearable: "",
            class: "form-select"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(INTERVIEW_STAGES), (label, value) => {
                return (_openBlock(), _createBlock(_component_el_option, {
                  key: value,
                  label: label,
                  value: value,
                  disabled: value === 'completed'
                }, null, 8, ["label", "value", "disabled"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]),
        _: 1
      }),
      _createVNode(_component_el_form_item, {
        label: "面试时间",
        prop: "dateTime",
        required: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_el_date_picker, {
            modelValue: formData.dateTime,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.dateTime) = $event)),
            type: "datetime",
            placeholder: "选择面试时间",
            format: "YYYY-MM-DD HH:mm",
            "value-format": "YYYY-MM-DDTHH:mm:00",
            "disabled-date": disabledDate,
            class: "form-date-picker"
          }, null, 8, ["modelValue"])
        ]),
        _: 1
      }),
      _createVNode(_component_el_row, { gutter: 20 }, {
        default: _withCtx(() => [
          _createVNode(_component_el_col, { span: 12 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_form_item, {
                label: "时长(分钟)",
                prop: "duration"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_input_number, {
                    modelValue: formData.duration,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.duration) = $event)),
                    min: 15,
                    max: 240,
                    step: 15,
                    class: "form-number-input"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, { span: 12 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_form_item, {
                label: "面试状态",
                prop: "status"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_select, {
                    modelValue: formData.status,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formData.status) = $event)),
                    placeholder: "请选择状态",
                    clearable: "",
                    class: "form-select"
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(INTERVIEW_STATUS), (label, value) => {
                        return (_openBlock(), _createBlock(_component_el_option, {
                          key: value,
                          label: label,
                          value: value
                        }, null, 8, ["label", "value"]))
                      }), 128))
                    ]),
                    _: 1
                  }, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_el_row, { gutter: 20 }, {
        default: _withCtx(() => [
          _createVNode(_component_el_col, { span: 12 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_form_item, {
                label: "面试地点",
                prop: "location"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_input, {
                    modelValue: formData.location,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.location) = $event)),
                    placeholder: "如：线上、公司会议室等",
                    clearable: "",
                    class: "form-input"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, { span: 12 }, {
            default: _withCtx(() => [
              _createVNode(_component_el_form_item, {
                label: "面试官",
                prop: "interviewer"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_input, {
                    modelValue: formData.interviewer,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formData.interviewer) = $event)),
                    placeholder: "面试官姓名",
                    clearable: "",
                    class: "form-input"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_el_form_item, {
        label: "准备材料",
        prop: "preparationMaterials"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_el_input, {
            modelValue: formData.preparationMaterials,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((formData.preparationMaterials) = $event)),
            type: "textarea",
            placeholder: "面试准备材料和注意事项",
            rows: 3,
            clearable: "",
            class: "form-textarea"
          }, null, 8, ["modelValue"])
        ]),
        _: 1
      }),
      _createVNode(_component_el_form_item, {
        label: "备注",
        prop: "notes"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_el_input, {
            modelValue: formData.notes,
            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((formData.notes) = $event)),
            type: "textarea",
            placeholder: "其他需要记录的信息",
            rows: 3,
            clearable: "",
            class: "form-textarea"
          }, null, 8, ["modelValue"])
        ]),
        _: 1
      }),
      (_ctx.editing)
        ? (_openBlock(), _createBlock(_component_el_form_item, {
            key: 0,
            label: "面试反馈",
            prop: "feedback"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_el_input, {
                modelValue: formData.feedback,
                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((formData.feedback) = $event)),
                type: "textarea",
                placeholder: "面试后的反馈和评价",
                rows: 3,
                clearable: "",
                class: "form-textarea"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_component_el_form_item, { class: "form-buttons" }, {
        default: _withCtx(() => [
          _createVNode(_component_el_button, {
            type: "primary",
            onClick: submitForm,
            loading: loading.value
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.editing ? '更新' : '创建'), 1)
            ]),
            _: 1
          }, 8, ["loading"]),
          _createVNode(_component_el_button, { onClick: resetForm }, {
            default: _withCtx(() => _cache[9] || (_cache[9] = [
              _createTextVNode("重置")
            ])),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["model", "rules"]))
}
}

})